import { Messages } from '../../../gen-src/Localize';
// eslint-disable-next-line import/no-cycle
import { FormatterMap, getActiveFormatters, getFormatterTypesOfKind } from './MarkupHelpers';

export const linkFormatter = '"":url';

/** `[startingIndex, selectionLength]` */
type SelectionPos = [number, number];

export type MarkupFunction = (
  pre: string,
  selection: string,
  post: string,
  formatter: string,
  textAreaElement: HTMLTextAreaElement,
  prepend?: string
) => MarkupFunctionResult;

interface MarkupFunctionResult {
  /** The string to replace the selection with, or insert at the cursor position if no selection */
  replacement: string;
  /** Index values for where to place the selection/cursor */
  selectionPos: SelectionPos;
}

const removeTrailingBlankLine = (lines: string[]): { lines: string[], linebreakWasRemoved: boolean } => {
  const lastLine = lines[lines.length - 1];
  const filteredLines = lines.slice();

  if (lines.length !== 1 && lastLine === '') {
    filteredLines.splice(filteredLines.length - 1, 1);
  }

  return {
    lines: filteredLines,
    linebreakWasRemoved: filteredLines.length < lines.length,
  };
};

const isLineBlankAndShouldNotBeFormatted = (line: string, lines: string[]): boolean => {
  return isBlank(line) && lines.length > 1;
};

const isBlank = (str: string): boolean => {
  return str.trim().length === 0;
};

const ensureTrailingLinebreak = (str: string, postString: string): string => {
  if (!str.endsWith('\n') && !postString.startsWith('\n')) {
    return str + '\n';
  }

  return str;
};

const ensureTrailingWhitespace = (str: string, postString: string): string => {
  if (
    !str.endsWith(' ') &&
    !str.endsWith('\n') &&
    postString.length > 0 &&
    !postString.startsWith(' ') &&
    !postString.startsWith('\n')
  ) {
    return str + ' ';
  }

  return str;
};

const ensureLeadingWhitespace = (str: string, preString: string): string => {
  if (
    !str.startsWith(' ') &&
    !str.startsWith('\n') &&
    preString.length > 0 &&
    !preString.endsWith(' ') &&
    !preString.endsWith('\n')
  ) {
    return ' ' + str;
  }

  return str;
};

const containsUrl = (str: string): boolean => {
  return str.indexOf('http') !== -1 || str.indexOf('www.') !== -1;
};

/**
 * Adds markup for formatters which only precede their content, as opposed to wrapping their content, eg: `p.` `h1.`
 * note: Does not toggle the formatter. Only adds new formatters`
 * @param preSelection the string directly preceding the selection
 * @param selection the current selection
 * @param preSelection the string directly following the selection
 * @param formatter the string responsible for designating the formatting/style of the content, eg: `p.` `h1.`
 * @param prepend an optional string to prepend directly before the formatter
 */
export const markupInsertFunction: MarkupFunction = (
  preSelection: string,
  selection: string,
  postSelection: string,
  formatter: string,
  textAreaElement: HTMLTextAreaElement,
  prepend: string = ' ',
): MarkupFunctionResult => {
  let result = (preSelection.endsWith('\n') || preSelection.endsWith(prepend) || isBlank(preSelection) ? '' : prepend);
  const selectionPos: SelectionPos = [0, 0];
  if (formatter === linkFormatter) {
    if (containsUrl(selection)) {
      result += `"${Messages.linkText()}":${selection}`;

      // select Messages.linkText()
      selectionPos[0] = preSelection.length + result.indexOf(Messages.linkText());
      selectionPos[1] = Messages.linkText().length;
    } else {
      result += `"${selection}":${Messages.linkUrl()}`;

      // select Messages.linkUrl()
      selectionPos[0] = preSelection.length + result.indexOf(Messages.linkUrl());
      selectionPos[1] = Messages.linkUrl().length;
    }

    // ensure result ends with a space character
    result = ensureTrailingWhitespace(result, postSelection);

    // For paragraph and heading formatters
  } else {
    if (isAnyInsertFormatterActive(preSelection, selection)) {
      const formatterTypeToRemove = getActiveFormatters(textAreaElement).find(type => FormatterMap[type].formatterApplicationType === 'insert');
      const formatterToRemove = formatterTypeToRemove && FormatterMap[formatterTypeToRemove].formatter || '';
      if (formatterTypeToRemove) {
        if (isActiveInsertFormatterPreSelected(preSelection, FormatterMap[formatterTypeToRemove].formatter)) {
          // Formatter precedes the selection
          // Expand selection to include formatters, so we can remove them with our `execCommand` method
          if (textAreaElement) {
            textAreaElement.setSelectionRange(preSelection.length - formatterToRemove.length, preSelection.length + selection.length);
          }
          if (formatterToRemove === formatter) {
            // Remove existing formatter
            result = selection;
            selectionPos[0] = preSelection.length - formatterToRemove.length;
            selectionPos[1] = result.length;
          } else {
            // Replace existing formatter with new one
            result = formatter + selection;
            selectionPos[0] = preSelection.length - formatterToRemove.length + formatter.length;
            selectionPos[1] = result.length - formatter.length;
          }

        } else if (isActiveInsertFormatterSelected(selection, formatterToRemove)) {
          // Formatter is included in selection
          if (formatterToRemove === formatter) {
            result = selection.slice(formatterToRemove.length);
            selectionPos[0] = preSelection.length;
            selectionPos[1] = result.length;
          } else {
            result = formatter + selection.slice(formatterToRemove.length);
            selectionPos[0] = preSelection.length;
            selectionPos[1] = result.length;
          }
        }
      }

    } else {
      // no formatter found, add it
      selectionPos[0] = preSelection.length + result.length + formatter.length;
      selectionPos[1] = selection.length; // place cursor after new formatter and blank space
      result += formatter + selection;
    }
  }

  return { replacement: result, selectionPos };
};

/**
 * Adds markup for formatters which are applied on multiple lines, eg: `*` `#`
 * note: Does not toggle the formatter. Only adds new formatters`
 * @param preSelection the string directly preceding the selection
 * @param selection the current selection
 * @param postSelection the string directly following the selection
 * @param formatter the string responsible for designating the formatting/style of the content, eg: `*` `#`
 */
export const markupMultilineFunction: MarkupFunction = (
  preSelection: string,
  selection: string,
  postSelection: string,
  formatter: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  textAreaElement: HTMLTextAreaElement,
): MarkupFunctionResult => {
  let result: string;

  const { lines, linebreakWasRemoved } = removeTrailingBlankLine(selection.split('\n'));
  const allLinesFormatted = lines.every((line) => isInsertFormatterActive('', line, formatter) || isLineBlankAndShouldNotBeFormatted(line, lines));

  if (allLinesFormatted) {
    // formatter applied to all lines, remove
    result = lines.map(line => line.substr(formatter.length)).join('\n');
  } else {
    // apply formatter to any lines that are missing it
    result = lines.map((line) => {
      if (isInsertFormatterActive('', line, formatter) || isLineBlankAndShouldNotBeFormatted(line, lines)) {
        return line;
      } else {
        return formatter + line;
      }
    }).join('\n');
  }

  if (!preSelection.endsWith('\n') && !isBlank(preSelection)) {
    result = '\n' + result;
  }

  if (linebreakWasRemoved) { result += '\n'; }

  result = ensureTrailingLinebreak(result, postSelection);

  const selectionPos: SelectionPos = [preSelection.length, result.length];

  return { replacement: result, selectionPos };
};

/**
 * Adds or removes markup for formatters which wrap their content, eg: `**` `__`
 * note: Toggles the formatter, depending on if one is already present in the selection
 * @param preSelection the string directly preceding the selection
 * @param selection the current selection
 * @param postSelection the string directly following the selection
 * @param formatter the string responsible for designating the formatting/style of the content, eg: `**` `__`
 */
export const markupWrappingFunction: MarkupFunction = (
  preSelection: string,
  selection: string,
  postSelection: string,
  formatter: string,
  textAreaElement: HTMLTextAreaElement,
): MarkupFunctionResult => {
  let result: string;
  let selectionPos: SelectionPos = [preSelection.length, 0];

  const isMultiLine = selection.indexOf('\n') !== -1;

  if (!isMultiLine) {
    if (isSelectionWrapped(selection, formatter)) {
      // selection already wrapped with formatter - remove it
      const replaced = selection.slice(formatter.length, -formatter.length);
      result = replaced;
      selectionPos = [preSelection.length, replaced.length];
    } else if (isSelectionWrappedAdjacent(preSelection, postSelection, formatter)) {
      // formatter is adjacent to selection - remove it
      if (textAreaElement) {
        // expand selection to include formatters, so we can easily remove them in one step
        textAreaElement.setSelectionRange(preSelection.length - formatter.length, preSelection.length + selection.length + formatter.length);
      }
      result = selection;
      selectionPos = [preSelection.length - formatter.length, selection.length];
    } else {
      // no formatter found - add it
      result = formatter + selection + formatter;
      result = ensureLeadingWhitespace(result, preSelection);
      const leadingLength = result.length - (formatter + selection + formatter).length;
      result = ensureTrailingWhitespace(result, postSelection);
      selectionPos = [preSelection.length + formatter.length + leadingLength, selection.length];
    }
  } else {
    const { lines, linebreakWasRemoved } = removeTrailingBlankLine(selection.split('\n'));

    const allLinesFormatted = lines.every((line, index) => {
      if (index === 0) {
        const indexFirstWrapped = isSelectionWrapped(line, formatter) || isSelectionWrapped(preSelection.substring(-formatter.length) + line, formatter);
        return indexFirstWrapped || isBlank(line);
      } else if (index === lines.length - 1) {
        const indexFinalWrapped = isSelectionWrapped(line, formatter) || isSelectionWrapped(line + postSelection.substr(0, formatter.length), formatter);
        return indexFinalWrapped;
      } else {
        return isSelectionWrapped(line, formatter) || isBlank(line);
      }
    });

    if (allLinesFormatted) {
      // each line wrapped with (or adjacent to) formatter - remove it
      result = lines.map((line, index) => {
        if (isSelectionWrapped(line, formatter)) {
          return line.slice(formatter.length, -formatter.length);
        } else if (index === 0) {
          textAreaElement.setSelectionRange(preSelection.length - formatter.length, preSelection.length + selection.length);
          selectionPos[0] = preSelection.length - formatter.length;
          return line.slice(0, -formatter.length);
        } else if (index === lines.length - 1) {
          const currentSelectionLength = textAreaElement.selectionEnd - textAreaElement.selectionStart;
          textAreaElement.setSelectionRange(textAreaElement.selectionStart, textAreaElement.selectionStart + currentSelectionLength + formatter.length);
          return line.slice(formatter.length);
        } else {
          return line;
        }
      }).join('\n');

      if (linebreakWasRemoved) { result += '\n'; }

      selectionPos[1] = result.length;
    } else {
      // add formatter to any lines missing them
      result = lines.map((line, index) => {
        if (isSelectionWrapped(line, formatter) || line.length === 0) {
          return line;
        } else if (index === 0) {
          if (isSelectionWrapped(preSelection.substring(-formatter.length) + line, formatter)) {
            return line;
          }
        } else if (index === lines.length - 1) {
          if (isSelectionWrapped(line + postSelection.substr(0, formatter.length), formatter)) {
            return line;
          }
        }

        return formatter + line + formatter;
      }).join('\n');

      if (linebreakWasRemoved) { result += '\n'; }

      result = ensureLeadingWhitespace(result, preSelection);
      result = ensureTrailingWhitespace(result, postSelection);

      selectionPos[1] = result.length;

      if (result.startsWith(' ')) {
        selectionPos[0]++;
        selectionPos[1]--;
      }
      if (result.endsWith(' ')) {
        selectionPos[1]--;
      }
    }
  }

  return { replacement: result, selectionPos };
};

export function isSelectionWrapped(selection: string, formatter: string): boolean {
  return selection.startsWith(formatter) && selection.endsWith(formatter) && selection.length > formatter.length;
}

export function isSelectionWrappedAdjacent(preSelection: string, postSelection: string, formatter: string): boolean {
  return preSelection.endsWith(formatter) && postSelection.startsWith(formatter);
}

function isActiveInsertFormatterSelected(selection: string, formatter: string): boolean {
  return selection.startsWith(formatter);
}

function isActiveInsertFormatterPreSelected(preSelection: string, formatter: string): boolean {
  return preSelection.endsWith(formatter);
}

export function isInsertFormatterActive(preSelection: string, selection: string, formatter: string): boolean {
  return isActiveInsertFormatterSelected(selection, formatter) || isActiveInsertFormatterPreSelected(preSelection, formatter);
}

function isAnyInsertFormatterActive(preSelection: string, selection: string): boolean {
  return getFormatterTypesOfKind('insert').some(value => isInsertFormatterActive(preSelection, selection, FormatterMap[value].formatter));
}
